import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Download, Users, UserCheck, MailCheck, Phone } from "lucide-react";
import { listContacts, downloadSubscribersCsv, NEWS, fmtDay } from "@/lib/contactsApi";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/brand/AppHeader";
import { MobileMenu } from "@/components/AppNav";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactDetail } from "@/components/contacts/ContactDetail";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, STATUS_LABEL, STATUS_TONE } from "@/lib/access";

const FILTERS = [["all", "All Contacts"], ["registered", "Registered Accounts"], ["subscribers", "Newsletter Subscribers"], ["not_subscribed", "Unsubscribed / Not Subscribed"], ["trainers", "Trainers"]];
const sel = "h-10 rounded-full border border-stone-300 bg-white px-3 text-xs font-semibold text-stone-700";

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4">
      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-stone-500 font-semibold"><Icon className="h-4 w-4 text-amber-800" />{label}</div>
      <div data-testid={testId} className="mt-1 font-display text-2xl sm:text-3xl font-bold text-stone-900">{value ?? "—"}</div>
    </div>
  );
}

function ContactRow({ c, i, onOpen }) {
  const [l, tone] = NEWS[c.newsletter_status];
  return (
    <li>
      <button type="button" data-testid={`contact-row-${i}`} onClick={onOpen} className="w-full text-left px-3 sm:px-4 py-3 hover:bg-stone-50 active:bg-stone-100 grid gap-1 sm:grid-cols-[1.4fr_1fr_0.9fr_0.9fr_0.8fr_auto] sm:items-center sm:gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-stone-900 truncate">{c.name || "—"}</div>
          <div className="text-xs text-stone-500 truncate">{c.email || "No email"}</div>
        </div>
        <div className="text-xs text-stone-600 flex items-center gap-1">{c.phone ? <><Phone className="h-3 w-3" />{c.phone}</> : <span className="text-stone-400">No phone</span>}</div>
        <div className="text-xs text-stone-600">{c.registered ? <>Registered {fmtDay(c.registered_at)}<br /><span className="text-stone-400">Last login {c.last_login_at ? fmtDay(c.last_login_at) : "—"}</span></> : <span className="text-stone-400">No account</span>}</div>
        <div className="text-xs">
          <span data-testid={`contact-news-${i}`} className={`inline-flex h-6 items-center px-2 rounded-full border font-semibold text-[11px] ${tone}`}>{l}</span>
          {c.consent_at && <div className="text-[11px] text-stone-500 mt-0.5">{fmtDay(c.consent_at)} · {c.consent_source}</div>}
        </div>
        <div className="text-xs flex flex-wrap gap-1" data-testid={`contact-account-${i}`}>
          {c.registered ? <>
            <span className="inline-flex h-6 items-center px-2 rounded-full border border-stone-300 font-semibold text-[11px]">{ROLE_LABEL[c.role] || c.role}</span>
            <span className={`inline-flex h-6 items-center px-2 rounded-full border font-semibold text-[11px] ${STATUS_TONE[c.status]}`}>{STATUS_LABEL[c.status]}</span>
          </> : <span className="text-stone-400">Contact only</span>}
          {(c.contact_roles || []).includes("trainer") && <span data-testid={`contact-trainer-${i}`} className="inline-flex h-6 items-center px-2 rounded-full border border-sky-200 bg-sky-50 text-sky-800 font-semibold text-[11px]">Trainer{c.trainer_horses?.length ? ` · ${c.trainer_horses.length} horse${c.trainer_horses.length === 1 ? "" : "s"}` : ""}</span>}
        </div>
        <div data-testid={`contact-apps-${i}`} className="text-xs text-stone-600 sm:text-right">{c.applications_count} application{c.applications_count === 1 ? "" : "s"}<br className="hidden sm:block" /><span className="sm:hidden"> · </span>{c.horses_count} horse{c.horses_count === 1 ? "" : "s"}</div>
      </button>
    </li>
  );
}

export default function Contacts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { setData(null); listContacts(filter).then(setData).catch(() => toast.error("Could not load contacts")); }, [filter, tick]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase(), digits = t.replace(/\D/g, "");
    return (data?.contacts || []).filter((c) => (!t || (c.name || "").toLowerCase().includes(t) || (c.email || "").includes(t) || (digits.length >= 3 && (c.phone || "").replace(/\D/g, "").includes(digits)))
      && (!role || (role === "none" ? !c.registered : c.role === role)) && (!status || c.status === status));
  }, [data, q, role, status]);
  const exportCsv = () => downloadSubscribersCsv().then(() => toast.success("Subscriber CSV downloaded")).catch(() => toast.error("Export failed"));
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <AppHeader title="Contacts" onBack={() => navigate("/")} backTestId="contacts-back" width="max-w-6xl">
          <Button data-testid="contacts-export" variant="outline" onClick={exportCsv} className="h-10 rounded-full text-xs sm:text-sm"><Download className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Export Subscribers (CSV)</span></Button>
          <MobileMenu />
      </AppHeader>
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-5 space-y-4">
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Stat icon={Users} label="Unique contacts" value={data?.stats.total} testId="contacts-stat-total" />
          <Stat icon={UserCheck} label="Registered accounts" value={data?.stats.registered} testId="contacts-stat-registered" />
          <Stat icon={MailCheck} label="Active subscribers" value={data?.stats.subscribers} testId="contacts-stat-subscribers" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto hide-scrollbar">
          {[...FILTERS, ...(isSuper ? [["deleted", `Deleted Contacts${data?.stats.deleted != null ? ` (${data.stats.deleted})` : ""}`]] : [])].map(([k, l]) => (
            <button key={k} type="button" data-testid={`contacts-filter-${k}`} aria-pressed={filter === k} onClick={() => setFilter(k)}
              className={`shrink-0 h-10 px-3.5 rounded-full border text-xs font-semibold ${filter === k ? "bg-stone-700 border-stone-700 text-white" : "bg-white border-stone-300 text-stone-700"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className="h-11 pl-9 rounded-full text-base sm:text-sm" />
          </div>
          <div className="flex gap-2">
            <select data-testid="contacts-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className={sel} aria-label="Filter by role">
              <option value="">All roles</option>{Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}<option value="none">No account</option>
            </select>
            <select data-testid="contacts-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={sel} aria-label="Filter by account status">
              <option value="">All statuses</option>{Object.entries(STATUS_LABEL).filter(([k]) => k !== "deleted").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        {!data ? <Skeleton className="h-40" /> : rows.length ? (
          <ul data-testid="contacts-list" className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100 overflow-hidden">
            {rows.map((c, i) => <ContactRow key={c.key || c.email} c={c} i={i} onOpen={() => setOpen(c.key || c.email)} />)}
          </ul>
        ) : <p data-testid="contacts-empty" className="text-sm text-stone-500 bg-white border border-stone-200 rounded-xl p-5">No contacts match.</p>}
      </main>
      {open && <ContactDetail email={open} onClose={() => setOpen(null)} onChanged={() => setTick((t) => t + 1)} />}
    </div>
  );
}
